import { lazy, Suspense, useState, useCallback, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn, Flex, Icon, Text } from '@kognitos/lattice';
import { useResolvedFileDisplayName } from '@/shared/hooks';
import type { FileCardProps } from '../../types';
import { getFileTypeInfo } from '../../utils/fileUpload';
import { joinTestIds } from '@/e2e/utils/test-utils';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import { downloadOutput } from '@/modules/outputs/utils/download';
import type { TResolvedFile } from '@/modules/outputs/types';

const FileHistoryPreview = lazy(() =>
  import('./FileHistoryPreview').then(m => ({
    default: m.FileHistoryPreview,
  })),
);

/**
 * Card component for displaying an uploaded file
 *
 * Shows file icon, name, and a remove button.
 * Icon and color are determined by file type/extension.
 * Fetches actual filename from server using file metadata API.
 *
 * @param props - Component props
 * @returns JSX element containing the file card
 */
export function FileCard({
  file,
  onRemove,
  showRemove = true,
  fileHistory,
  onSelectHistoryFile,
}: FileCardProps) {
  const { t } = useTranslation('automation-inputs');
  const [selectedFile, setSelectedFile] = useState<TResolvedFile | null>(null);

  const { displayName, isLoading } = useResolvedFileDisplayName(
    file.resourceName,
    file.name,
  );
  const fileType = getFileTypeInfo(displayName);
  const hasFileHistory = fileHistory && fileHistory.length > 0;

  /**
   * Handles clicking on the file card to open preview.
   * Prevents opening when clicking on the remove button.
   */
  const handleFileClick = useCallback(() => {
    if (file.resourceName) {
      const fileValue: TResolvedFile = {
        type: 'file-remote',
        fileUrl: file.resourceName,
      };
      setSelectedFile(fileValue);
    }
  }, [file.resourceName]);

  /**
   * Handles clicking on the remove button.
   * Prevents triggering file preview when removing the file.
   */
  const handleRemoveClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation(); // Prevent triggering file preview
      onRemove();
    },
    [onRemove],
  );

  return (
    <>
      <Flex
        align="center"
        data-testid={joinTestIds('file-card', displayName)}
        gap={3}
        className={cn(
          'flex items-center gap-3 p-2 bg-muted rounded-xl border-2 border-dashed border-border',
          'cursor-pointer hover:bg-muted/80 transition-colors',
        )}
        onClick={handleFileClick}
      >
        {/* File icon */}
        <Flex
          align="center"
          justify="center"
          className={`p-2 rounded-sm ${fileType.colorClass}`}
        >
          <Icon type={fileType.icon} size="sm" />
        </Flex>

        {/* File name */}
        <Flex flex="1" className="min-w-0">
          <Text level="base" className="truncate font-normal">
            {isLoading ? t('status.loading') : displayName}
          </Text>
        </Flex>

        {/* Remove button - shown when showRemove is true */}
        {showRemove && (
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={handleRemoveClick}
            className="flex-shrink-0 p-1 hover:bg-muted/80 rounded transition-colors"
            asChild
          >
            <Icon
              type="X"
              size="sm"
              className="text-muted-foreground hover:text-foreground transition-colors"
            />
          </Button>
        )}
      </Flex>
      {hasFileHistory ? (
        <Suspense fallback={null}>
          <FileHistoryPreview
            isOpen={selectedFile !== null}
            onClose={() => setSelectedFile(null)}
            currentFile={selectedFile!}
            currentFileValue={{ file: { remote: file.resourceName } }}
            fileHistory={fileHistory!}
            onSelectVersion={value => {
              onSelectHistoryFile?.(value);
              setSelectedFile(null);
            }}
          />
        </Suspense>
      ) : (
        <PreviewLayover
          isOpen={selectedFile !== null}
          outputName={displayName}
          outputValue={selectedFile}
          onClose={() => setSelectedFile(null)}
          onDownload={downloadOutput}
        />
      )}
    </>
  );
}
